import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Carousel from 'react-bootstrap/Carousel';
import withGameLeaderboardData from 'containers/withGameLeaderboardData';
import A11yFontIcon from 'components/A11yFontIcon';
import LoadingIndicator from 'components/LoadingIndicator';
import PaginationControls from 'components/PaginationControls';
import ProfileIcon from 'components/ProfileIcon';
import { formatNumber } from 'utils/formatting';
import {
  displayUserShape,
  errorShape,
  gameShape,
  userShape
} from 'utils/shapes';
import withDelayedMount from 'utils/withDelayedMount';
import withMediaQuery from 'utils/withMediaQuery';
import LeaderdBoardIconActive from 'resources/Button-Leaderboard-Active.png';
import './GameLeaderboard.css';

const REFRESH_DELAY = 10000;

const SM_BREAKPOINT_AND_DOWN = '(max-width: 480px)';
class GameLeaderboard extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      refreshTimeoutId: undefined,
      activeIndex: 0,
      buttonsEnabled: true
    };

    this.onRefresh = this.onRefresh.bind(this);
    this.handleRefreshTimeout = this.handleRefreshTimeout.bind(this);
  }

  componentWillUnmount() {
    clearTimeout(this.state.refreshTimeoutId);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.show && nextProps.show) {
      this.onRefresh();
    }
  }

  handleRefreshTimeout() {
    this.setState({ refreshTimeoutId: undefined });
  }

  onRefresh() {
    if (!this.state.refreshTimeoutId) {
      this.props.handleRefresh && this.props.handleRefresh();
      const timeoutId = setTimeout(this.handleRefreshTimeout, REFRESH_DELAY);
      this.setState({ refreshTimeoutId: timeoutId });
    }
  }

  setPage = newPage => {
    if (newPage !== this.state.activeIndex) {
      const newState = { activeIndex: newPage };
      this.setState(newState);
    }
  };

  render() {
    const {
      show,
      gameRankingsData,
      gameData,
      handleClose,
      userData,
      matchesBreakpoint
    } = this.props;
    const { refreshTimeoutId, activeIndex, buttonsEnabled } = this.state;

    if (!show) {
      return null;
    }

    let content = null;
    if (gameRankingsData.error) {
      content = (
        <div className="leaderboard-error">
          {'An error ocurred while retrieving the leaderboards.'}
          {' Please try again later.'}
        </div>
      );
    } else if (gameRankingsData.loading) {
      content = (
        <div className="leaderboard-loading">
          <LoadingIndicator expand />
        </div>
      );
    } else {
      const boardList = [
        { title: 'Daily', data: gameRankingsData.daily, user: userData?.user },
        { title: 'Weekly', data: gameRankingsData.weekly },
        { title: 'Lifetime', data: gameRankingsData.lifetime }
      ];
      if (matchesBreakpoint) {
        content = (
          <React.Fragment>
            <div className="game-leaderboard-pagination">
              <PaginationControls
                controls={boardList}
                activeIndex={activeIndex}
                onPaginationChange={this.setPage}
                withSound={false}
                buttonsEnabled={buttonsEnabled}
                displayControlTitle={true}
                displayControlTitleKey={'title'}
              />
            </div>
            <Carousel
              className="game-leaderboard-carousel"
              controls={false}
              interval={null}
              indicators={false}
              activeIndex={activeIndex}
              onSlid={() => {}}
              key={boardList.length} // Workaround for Carousel stuck index bug
            >
              {boardList.map((board, index) => (
                <Carousel.Item key={`key-${board.title}`}>
                  <div key="rankings" className="leaderboard-rankings">
                    {renderLeaderboardRankings(board)}
                  </div>
                  <div key="self" className="leaderboard-self-rankings">
                    {renderLeaderboardSelfRanking(board)}
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </React.Fragment>
        );
      } else {
        content = [
          <div key="titles" className="leaderboard-titles">
            {boardList.map(renderLeaderboardTitle)}
          </div>,
          <div key="rankings" className="leaderboard-rankings">
            {boardList.map(renderLeaderboardRankings)}
          </div>,
          <div key="self" className="leaderboard-self-rankings">
            {boardList.map(renderLeaderboardSelfRanking)}
          </div>
        ];
      }
    }

    const refreshClass =
      'refresh-button' + (refreshTimeoutId ? ' disabled' : '');
    return (
      <div className="game-leaderboard">
        <div className="leaderboard-header">
          <img src={LeaderdBoardIconActive} alt={'Open Leaderboard'} />
          <div className="leaderboard-header-display">
            {gameData.displayName} Leaderboard
          </div>
          {!gameRankingsData.error && (
            <button
              type="button"
              onClick={this.onRefresh}
              className={`btn ${refreshClass}`}
            >
              <A11yFontIcon
                icon="refresh"
                large
                spin={!!refreshTimeoutId}
                alt="Refresh Leaderboard"
              />
            </button>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="btn leaderboard-close-button"
          >
            <A11yFontIcon icon="close" large alt="Close Leaderboard" />
          </button>
        </div>
        {content}
      </div>
    );
  }
}

function renderLeaderboardTitle(board) {
  return (
    <div className="leaderboard-title" key={board.title}>
      {board.title}
    </div>
  );
}

function renderLeaderboardRankings(board) {
  return (
    <div className="leaderboard-rankings-column" key={board.title}>
      {board.data.leaderboard.map(renderLeaderboardRanking)}
    </div>
  );
}

function renderLeaderboardRanking(ranking) {
  return (
    <div className="leaderboard-ranking" key={ranking.player.userId}>
      <div className="ranking-name" title={ranking.player.displayName}>
        {ranking.player.displayName}
      </div>
      <div className="ranking-points">{formatNumber(ranking.score)} pts</div>
      <div className="ranking-rank">{formatNumber(ranking.rank)}</div>
    </div>
  );
}

function renderLeaderboardSelfRanking(board) {
  const selfRanking = board.data.selfRanking;
  const score = selfRanking ? selfRanking.score : 0;
  const rankText = selfRanking ? formatNumber(selfRanking.rank) : '-';

  return (
    <div className="leaderboard-self" key={board.title}>
      {board.user && (
        <>
          <div className="leaderboard-self-label">
            <ProfileIcon
              withBorder
              avatarData={board.user.avatarData}
              size={30}
            />
          </div>
          <div className="leaderboard-self-display-name">
            {board.user?.displayName}
          </div>
        </>
      )}
      <div className="leaderboard-self-score">{formatNumber(score)} pts</div>
      <div className="leaderboard-self-rank">
        <div className="rank-bubble">{rankText}</div>
      </div>
    </div>
  );
}

const gameRankShape = PropTypes.shape({
  rank: PropTypes.number,
  score: PropTypes.number,
  player: displayUserShape
});

const gameRankingsShape = PropTypes.shape({
  leaderboard: PropTypes.arrayOf(gameRankShape),
  selfRanking: gameRankShape
});

GameLeaderboard.propTypes = {
  show: PropTypes.bool,
  gameRankingsData: PropTypes.shape({
    daily: gameRankingsShape,
    weekly: gameRankingsShape,
    lifetime: gameRankingsShape,
    loading: PropTypes.bool,
    error: errorShape
  }),
  gameData: gameShape,
  user: userShape,
  handleRefresh: PropTypes.func,
  handleClose: PropTypes.func
};

export default withDelayedMount(props => props.show)(
  withMediaQuery(SM_BREAKPOINT_AND_DOWN)(
    withGameLeaderboardData(GameLeaderboard)
  )
);
